import * as React from "react";
import { toast } from "react-toastify";
import {
  useGetOrganizationLlmModelConfigQuery,
  useUpdateOrganizationLlmModelConfigMutation,
} from "@/api/organizations-admin";
import type { LlmModelProvider, LlmModelProcess, LlmProcessModelConfig } from "@/types/organization";
import { getCustomLlmModelError } from "@/components/modals/organizationSettings/helpers";

interface Params {
  open: boolean;
  organizationId: string | undefined;
}

// Selections live in the modal (not the tab) because Radix tabs unmount inactive content and
// unsaved choices must survive switching tabs.
export const useLlmModelSettings = ({ open, organizationId }: Params) => {
  const {
    currentData: llmModelConfig,
    isLoading: isLoadingInitialLlmModelConfig,
    isFetching: isFetchingLlmModelConfig,
  } = useGetOrganizationLlmModelConfigQuery(organizationId ?? "", {
    skip: !open || !organizationId,
  });
  const isLoadingLlmModelConfig = isLoadingInitialLlmModelConfig || isFetchingLlmModelConfig;

  const [updateLlmModelConfig, { isLoading: isUpdatingLlmModelConfig }] = useUpdateOrganizationLlmModelConfigMutation();

  const [selectedLlmModels, setSelectedLlmModels] = React.useState<Partial<Record<LlmModelProcess, string>>>({});
  const [selectedLlmModelProviders, setSelectedLlmModelProviders] = React.useState<
    Partial<Record<LlmModelProcess, LlmModelProvider>>
  >({});

  React.useEffect(() => {
    setSelectedLlmModels({});
    setSelectedLlmModelProviders({});
  }, [organizationId]);

  React.useEffect(() => {
    if (!llmModelConfig) return;
    setSelectedLlmModels(
      llmModelConfig.processes.reduce(
        (acc, process) => ({
          ...acc,
          [process.process]: process.model,
        }),
        {} as Partial<Record<LlmModelProcess, string>>,
      ),
    );
    setSelectedLlmModelProviders(
      llmModelConfig.processes.reduce(
        (acc, process) => ({
          ...acc,
          [process.process]: process.provider,
        }),
        {} as Partial<Record<LlmModelProcess, LlmModelProvider>>,
      ),
    );
  }, [llmModelConfig]);

  const getSelectedLlmModel = (process: LlmProcessModelConfig) => selectedLlmModels[process.process] ?? process.model;

  const getKnownLlmModelOption = (process: LlmProcessModelConfig, model: string) =>
    process.availableModels.find(option => option.model === model);

  const getSelectedLlmProvider = (process: LlmProcessModelConfig, model: string) =>
    getKnownLlmModelOption(process, model)?.provider ?? selectedLlmModelProviders[process.process] ?? process.provider;

  const isCustomLlmModel = (process: LlmProcessModelConfig, model: string) =>
    getKnownLlmModelOption(process, model) === undefined;

  const invalidLlmModelProcess = llmModelConfig?.processes.find(process => {
    const selectedModel = getSelectedLlmModel(process);
    return isCustomLlmModel(process, selectedModel) && getCustomLlmModelError(selectedModel) !== null;
  });

  const onLlmModelSubmit = async () => {
    if (!organizationId || !llmModelConfig) return;
    if (invalidLlmModelProcess) {
      toast.error(`Enter a valid custom model name for ${invalidLlmModelProcess.label}`);
      return;
    }
    try {
      await updateLlmModelConfig({
        id: organizationId,
        data: {
          configs: llmModelConfig.processes.map(process => {
            const selectedModel = getSelectedLlmModel(process).trim();
            const selectedOption = getKnownLlmModelOption(process, selectedModel);
            return {
              process: process.process,
              model: selectedModel === process.defaultModel ? null : selectedModel,
              provider:
                selectedModel === process.defaultModel
                  ? null
                  : (selectedOption?.provider ?? getSelectedLlmProvider(process, selectedModel)),
            };
          }),
        },
      }).unwrap();
      toast.success("AI model settings updated");
    } catch (error) {
      console.error("Failed to update AI model settings:", error);
      toast.error("Failed to update AI model settings");
    }
  };

  return {
    llmModelConfig,
    isLoadingLlmModelConfig,
    isUpdatingLlmModelConfig,
    setSelectedLlmModels,
    setSelectedLlmModelProviders,
    getSelectedLlmModel,
    getKnownLlmModelOption,
    getSelectedLlmProvider,
    isCustomLlmModel,
    invalidLlmModelProcess,
    onLlmModelSubmit,
  };
};

export type LlmModelSettings = ReturnType<typeof useLlmModelSettings>;
